import { defineStore } from 'pinia'
import { registerTenantScopedReset } from '@/stores/_tenantScopedRegistry'
import { computed, ref } from 'vue'
import { aiChatApi } from '@/api/aiChat'
import { useAiChatStore } from '@/stores/aiChat'
import { useAiPlanStore } from '@/stores/aiPlan'

/** 记住上次打开的会话，刷新后仍能自动打开同一条线程。 */
const ACTIVE_SESSION_STORAGE_KEY = 'smart-campus-ai-active-session'

/** sessionId → 置顶时刻（ms），仅存仍希望置顶的会话。 */
const PINNED_SESSIONS_STORAGE_KEY = 'smart-campus-ai-pinned-sessions'

function persistActiveSessionId(id: string | null): void {
  if (id) {
    sessionStorage.setItem(ACTIVE_SESSION_STORAGE_KEY, id)
  } else {
    sessionStorage.removeItem(ACTIVE_SESSION_STORAGE_KEY)
  }
}

function loadPinnedAtBySession(): Map<string, number> {
  try {
    const raw = localStorage.getItem(PINNED_SESSIONS_STORAGE_KEY)
    if (!raw) {
      return new Map()
    }
    const obj = JSON.parse(raw) as Record<string, number>
    return new Map(
      Object.entries(obj).filter(
        ([, t]) => typeof t === 'number' && Number.isFinite(t)
      )
    )
  } catch {
    return new Map()
  }
}

function savePinnedAtBySession(map: Map<string, number>): void {
  if (map.size === 0) {
    localStorage.removeItem(PINNED_SESSIONS_STORAGE_KEY)
    return
  }
  localStorage.setItem(PINNED_SESSIONS_STORAGE_KEY, JSON.stringify(Object.fromEntries(map)))
}

/** 删掉已不存在的 sessionId，避免本地无限增长 */
function prunePinnedStorage(validIds: ReadonlySet<string>): void {
  const map = loadPinnedAtBySession()
  let changed = false
  for (const id of map.keys()) {
    if (!validIds.has(id)) {
      map.delete(id)
      changed = true
    }
  }
  if (changed) {
    savePinnedAtBySession(map)
  }
}

function persistPin(id: string, pinnedAt: number): void {
  const map = loadPinnedAtBySession()
  map.set(id, pinnedAt)
  savePinnedAtBySession(map)
}

function persistUnpin(id: string): void {
  const map = loadPinnedAtBySession()
  if (!map.delete(id)) {
    return
  }
  savePinnedAtBySession(map)
}

export interface AiChatSessionItem {
  id: string
  title: string
  /** 首条消息流式期间：标题摘要尚未从 done 写入时为 true，侧边栏显示加载动画 */
  titlePending?: boolean
  updatedAt: number
  pinned: boolean
  pinnedAt: number | null
  /**
   * 该会话对应的 AI 任务是否仍在后端跑（与前端订阅无关）。
   * 业务上用来在侧边栏把"删除按钮"替换成"转动圆圈"。
   */
  running?: boolean
  /**
   * 该会话等待用户处理的待办卡数量（服务端权威值，刷新后依然可见）。
   * 与本地 userActionQueue 取并集驱动"需要操作"铃铛。
   */
  pendingActionCount?: number
}

export interface RegisterServerSessionOptions {
  /** 为 true 时显示标题生成中（三点动画），忽略 title */
  titlePending?: boolean
  /**
   * 为 false 时不要把侧边栏选中项切到该会话。
   * 用户已点「新对话」或切走时，后台流的 session/title 事件不能把视图抢回来。
   */
  activate?: boolean
}

/**
 * 侧边栏会话列表：流式首包 {@code session} 事件携带真实 sessionId 后即 registerServerSession；
 * 与「整表拉取」竞态时合并保留本地尚未出现在列表中的 id。
 */
export const useAiSessionsStore = defineStore('aiSessions', () => {
  const sessions = ref<AiChatSessionItem[]>([])
  /** null = 新对话草稿，尚未在后端落会话 */
  const activeSessionId = ref<string | null>(null)

  const isDraft = computed(() => activeSessionId.value === null)

  const pinnedSessions = computed(() =>
    [...sessions.value]
      .filter((s) => s.pinned)
      .sort((a, b) => (b.pinnedAt ?? 0) - (a.pinnedAt ?? 0))
  )

  const recentSessions = computed(() =>
    [...sessions.value]
      .filter((s) => !s.pinned)
      .sort((a, b) => b.updatedAt - a.updatedAt)
  )

  function selectSession(id: string): void {
    activeSessionId.value = id
    persistActiveSessionId(id)
  }

  /** 进入空白草稿 */
  function startNewConversation(): void {
    activeSessionId.value = null
    persistActiveSessionId(null)
  }

  /**
   * 清空全部会话列表与本地持久化（登出 / 切换租户时调用）。
   *
   * <p>否则上一个租户的会话会残留在侧边栏：loadFromServer 的 preservedLocal 合并逻辑
   * 会把本地残留会话保留下来，造成跨租户数据泄漏。</p>
   */
  function resetAll(): void {
    sessions.value = []
    activeSessionId.value = null
    sessionStorage.removeItem(ACTIVE_SESSION_STORAGE_KEY)
    localStorage.removeItem(PINNED_SESSIONS_STORAGE_KEY)
  }

  function registerServerSession(
    sessionId: string,
    title?: string | null,
    options?: RegisterServerSessionOptions
  ): void {
    const now = Date.now()
    const pending = options?.titlePending === true
    const existing = sessions.value.find((s) => s.id === sessionId)
    const activate = options?.activate !== false

    if (existing) {
      // 已有会话：保留已有的 running 状态，不强行覆盖。
      // 任务结束时由 SSE run_state 事件触发 markEnded 来收尾；
      // SSE 短暂断连时也不会把已经在转的会话误判为已结束。
      const updated: AiChatSessionItem = {
        ...existing,
        updatedAt: now,
        titlePending: pending,
        ...(pending
          ? (title?.trim() ? { title: title.trim() } : {})
          : { title: title?.trim() || '新对话' })
      }
      sessions.value = sessions.value.map(s => (s.id === sessionId ? updated : s))
    } else {
      // 新建会话：用户刚发了消息，AI 任务此时一定还在后端跑。
      // 乐观标记 running=true，避免依赖 SSE run_state 事件（新建会话场景下前端
      // EventSource 订阅者可能晚于后端 markRunning 广播，导致事件丢失）。
      // 任务结束由 SSE run_state 事件触发 markEnded 关闭。
      // 极端情况（断网/崩溃）下，刷新页面时 loadFromServer 会从后端 runRegistry
      // 重新读取真实状态来修正。
      sessions.value = [
        {
          id: sessionId,
          title: title?.trim() || (pending ? '' : '新对话'),
          titlePending: pending,
          running: true,
          updatedAt: now,
          pinned: false,
          pinnedAt: null
        },
        ...sessions.value.filter((s) => s.id !== sessionId)
      ]
    }
    if (activeSessionId.value === sessionId) {
      persistActiveSessionId(sessionId)
      return
    }
    if (!activate || activeSessionId.value != null) {
      return
    }
    activeSessionId.value = sessionId
    persistActiveSessionId(sessionId)
  }

  /** 流式失败等：取消「标题生成中」，回退为「新对话」 */
  function abandonSessionTitleGenerating(sessionId: string | null | undefined): void {
    if (!sessionId) {
      return
    }
    const s = sessions.value.find((x) => x.id === sessionId)
    if (!s?.titlePending) {
      return
    }
    sessions.value = sessions.value.map(x =>
      x.id === sessionId ? { ...x, titlePending: false, title: '新对话' } : x
    )
  }

  /**
   * 把会话标记为「任务仍在后端跑」——侧边栏会把删除按钮换成转动圆圈。
   * 由发送/续跑入口立刻调用，不依赖 run_start 或 /ai/events（两者都经常晚于用户看到列表）。
   * 若侧边栏还没有这条会话（首条消息），先插入一条 running 占位，避免静默 no-op。
   */
  function markRunning(sessionId: string | null | undefined): void {
    if (!sessionId) {
      return
    }
    const s = sessions.value.find((x) => x.id === sessionId)
    if (!s) {
      sessions.value = [
        {
          id: sessionId,
          title: '',
          titlePending: true,
          running: true,
          updatedAt: Date.now(),
          pinned: false,
          pinnedAt: null
        },
        ...sessions.value
      ]
      return
    }
    if (s.running) {
      return
    }
    sessions.value = sessions.value.map(x =>
      x.id === sessionId ? { ...x, running: true } : x
    )
  }

  /**
   * 把会话标记为「任务已结束（成功/失败/AI 主动停 都收敛到这一种）」。
   * 侧边栏转动圆圈恢复为删除按钮。
   */
  function markEnded(sessionId: string | null | undefined): void {
    if (!sessionId) {
      return
    }
    const s = sessions.value.find((x) => x.id === sessionId)
    if (!s || !s.running) {
      return
    }
    sessions.value = sessions.value.map(x =>
      x.id === sessionId ? { ...x, running: false } : x
    )
  }

  /**
   * 从服务端拉取历史会话填充侧边栏，并尽力恢复置顶状态与上次打开的会话。
   */
  async function loadFromServer(): Promise<void> {
    try {
      const res = await aiChatApi.listSessions()
      const rows = res.data ?? []
      const chatStore = useAiChatStore()
      const memPin = new Map(
        sessions.value.filter((s) => s.pinned).map((s) => [s.id, s.pinnedAt ?? Date.now()] as const)
      )
      const diskPin = loadPinnedAtBySession()
      const pinnedAtFor = (sessionId: string): number | null => {
        const fromDisk = diskPin.get(sessionId)
        const fromMem = memPin.get(sessionId)
        if (fromDisk == null && fromMem == null) {
          return null
        }
        return Math.max(fromDisk ?? 0, fromMem ?? 0)
      }
      const serverIds = new Set(rows.map((r) => r.sessionId))
      const fromServer = rows.map((r) => {
        const pinnedAt = pinnedAtFor(r.sessionId)
        const serverTitle = r.title?.trim() || '新对话'
        const local = sessions.value.find((s) => s.id === r.sessionId)
        const serverPending = r.titlePending === true
        const serverRunning = r.running === true
        // 只信「本地确实还在跑」（当前 SSE / 待办队列）或服务端 registry。
        // 不再用残留的 item.running 盖住服务端已结束，否则确认后续跑断开后圈会一直转。
        const locallyLive = chatStore.isSessionTaskOpen(r.sessionId)
        const running = locallyLive || serverRunning
        if (local?.titlePending) {
          return {
            id: r.sessionId,
            title: serverPending ? local.title || serverTitle : serverTitle,
            titlePending: serverPending,
            updatedAt: new Date(r.updatedAt).getTime(),
            pinned: pinnedAt != null,
            pinnedAt: pinnedAt ?? null,
            running,
            pendingActionCount: r.pendingActionCount ?? 0
          }
        }
        return {
          id: r.sessionId,
          title: serverTitle,
          titlePending: serverPending,
          updatedAt: new Date(r.updatedAt).getTime(),
          pinned: pinnedAt != null,
          pinnedAt: pinnedAt ?? null,
          running,
          pendingActionCount: r.pendingActionCount ?? 0
        }
      })
      const preservedLocal = sessions.value.filter((s) => !serverIds.has(s.id))
      sessions.value = [...fromServer, ...preservedLocal.map((s) => ({ ...s }))]

      prunePinnedStorage(new Set(sessions.value.map((s) => s.id)))

      const cur = activeSessionId.value
      if (cur != null && !sessions.value.some((s) => s.id === cur)) {
        activeSessionId.value = null
        persistActiveSessionId(null)
      }

      const stored = sessionStorage.getItem(ACTIVE_SESSION_STORAGE_KEY)
      if (
        stored &&
        sessions.value.some((s) => s.id === stored) &&
        activeSessionId.value == null
      ) {
        activeSessionId.value = stored
      }
    } catch {
      /* 401 等交由 http 层；保留本地占位状态 */
    }
  }

  function pinSession(id: string): void {
    const s = sessions.value.find((x) => x.id === id)
    if (!s || s.pinned) {
      return
    }
    const now = Date.now()
    s.pinned = true
    s.pinnedAt = now
    persistPin(id, now)
  }

  function unpinSession(id: string): void {
    const s = sessions.value.find((x) => x.id === id)
    if (!s || !s.pinned) {
      return
    }
    s.pinned = false
    s.pinnedAt = null
    persistUnpin(id)
  }

  /**
   * 物理删除服务端会话与本条侧边栏记录；失败时返回 false（本地列表不变）。
   */
  async function removeSession(id: string): Promise<boolean> {
    try {
      await aiChatApi.deleteSession(id)
    } catch {
      return false
    }

    const next = sessions.value.filter((s) => s.id !== id)
    if (next.length === sessions.value.length) {
      return false
    }
    sessions.value = next
    persistUnpin(id)
    if (activeSessionId.value === id) {
      activeSessionId.value = next[0]?.id ?? null
    }
    persistActiveSessionId(activeSessionId.value)

    const chatStore = useAiChatStore()
    chatStore.clearSessionCache(id)
    const planStore = useAiPlanStore()
    planStore.resetForSession(id)

    return true
  }

  return {
    sessions,
    activeSessionId,
    isDraft,
    pinnedSessions,
    recentSessions,
    selectSession,
    startNewConversation,
    resetAll,
    registerServerSession,
    abandonSessionTitleGenerating,
    markRunning,
    markEnded,
    loadFromServer,
    removeSession,
    pinSession,
    unpinSession
  }
})

registerTenantScopedReset('aiSessions', () => useAiSessionsStore().resetAll())
